import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ParentIframeManager } from "../core";
import type {
  IframeConfig,
  IframeVisibility,
  IframeMessage,
  MessageHandler,
} from "../core";

interface ParentIframeContextValue {
  setVisibility: (visibility: IframeVisibility) => void;
  navigate: (route: string) => void;
  sendMessage: (message: IframeMessage) => void;
  onMessage: (handler: MessageHandler) => () => void;
  isReady: boolean;
  visibility: IframeVisibility;
  debug?: (message: string, ...args: any[]) => void;
}

const ParentIframeContext = createContext<ParentIframeContextValue | null>(
  null
);

export interface ParentIframeProviderProps {
  config: IframeConfig;
}

export function ParentIframeProvider({
  children,
  config,
}: React.PropsWithChildren<ParentIframeProviderProps>) {
  const managerRef = useRef<ParentIframeManager | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [childReady, setChildReady] = useState(false);

  // Both parent iframe AND child app must be ready
  const isReady = iframeReady && childReady;

  useEffect(() => {
    const manager = new ParentIframeManager();
    managerRef.current = manager;

    manager.initialize(config).then(() => {
      setIframeReady(true);
    });

    // Listen for child ready message
    const unsubscribe = manager.onMessageFromChild((message) => {
      // Only log if consumer provided debug callback
      if (config.debug) {
        config.debug(
          "[PaywallParent] iframe-messenger-v2: Message from child:",
          message
        );
      }
      if (message.type === "IFRAME_READY") {
        setChildReady(true);
      }
    });

    return () => {
      unsubscribe();
      manager.destroy();
      managerRef.current = null;
      setIframeReady(false);
      setChildReady(false);
    };
  }, [config]);

  const setVisibility = (visibility: IframeVisibility) => {
    managerRef.current?.setVisibility(visibility);
  };

  const navigate = (route: string) => {
    // Reset child ready state before navigation so it can be re-triggered
    setChildReady(false);

    // Send navigation message instead of changing iframe.src
    sendMessage({
      type: "NAVIGATE_TO_ROUTE",
      payload: { route },
    });
  };

  const sendMessage = (message: IframeMessage) => {
    managerRef.current?.sendMessageToChild(message);
  };

  const onMessage = (handler: MessageHandler) => {
    return managerRef.current?.onMessageFromChild(handler) || (() => {});
  };

  const contextValue: ParentIframeContextValue = {
    setVisibility,
    navigate,
    sendMessage,
    onMessage,
    isReady,
    visibility: managerRef.current?.getVisibility() || "hidden",
    debug: config.debug,
  };

  return (
    <ParentIframeContext.Provider value={contextValue}>
      {children}
    </ParentIframeContext.Provider>
  );
}

export function useParentIframe(): ParentIframeContextValue {
  const context = useContext(ParentIframeContext);
  if (!context) {
    throw new Error("useParentIframe must be used within ParentIframeProvider");
  }
  return context;
}
